import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { configStepApi, configOptionApi, configDependencyApi, productConfigApi } from '../api';
import { ConfigStep, ConfigOption, ConfigDependency, SelectedOption } from '../types';
import { useConfigStore, isSelectionComplete } from '../store/useConfigStore';
import { objectParticle } from '../utils/format';
import { DEFAULT_LANG, Lang } from '../config/locale';
import Header from '../components/Header';
import StepProgress from '../components/StepProgress';
import OptionCard from '../components/OptionCard';
import ChatFab from '../components/ChatFab';

interface ConfiguratorPageProps {
  onComplete: (selections: any[], totalPrice: number) => void;
  onLogoClick?: () => void;
  lang?: Lang;
  onLangChange?: (lang: Lang) => void;
}

// URL에서 사용하는 step_key 목록
const STEP_KEYS = [
  'power_type', 'power_source', 'motor_origin', 'motor_capacity',
  'frame_type', 'frame_sub_type', 'engine_brand', 'engine_power',
  'start_method', 'pump', 'pump_option', 'hose',
];

const ConfiguratorPage: React.FC<ConfiguratorPageProps> = ({ onComplete, onLogoClick, lang = DEFAULT_LANG, onLangChange }) => {
  const store = useConfigStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [availableOptions, setAvailableOptions] = useState<ConfigOption[]>([]);
  const [allOptions, setAllOptions] = useState<ConfigOption[]>([]);
  const [dependencies, setDependencies] = useState<ConfigDependency[]>([]);
  // 유효 제품 조합(옵션ID 집합) - 가용옵션 역산용. 제품으로 이어지지 않는 막다른 경로 차단.
  const [configSets, setConfigSets] = useState<Set<number>[]>([]);
  const [loading, setLoading] = useState(true);
  const restoredRef = useRef(false);
  // 복원 중 최종 페이지로 이동한 경우, Store→URL 동기화가 page=final을 덮어쓰지 않도록 차단
  const skipUrlSyncRef = useRef(false);

  // 초기 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      try {
        const [stepsRes, optionsRes, depsRes, setsRes] = await Promise.all([
          configStepApi.getAll(),
          configOptionApi.getAll(),
          configDependencyApi.getAll(),
          // 구버전 서버엔 /all 엔드포인트가 없을 수 있음 → 실패해도 의존성 폴백으로 동작하도록 방어
          productConfigApi.all().catch(() => ({ data: [] as { option_ids: number[] }[] })),
        ]);
        store.setAllSteps(stepsRes.data);
        setAllOptions(optionsRes.data);
        setDependencies(depsRes.data);
        setConfigSets((setsRes.data || []).map((c: { option_ids: number[] }) => new Set(c.option_ids)));
      } catch (err) {
        console.error('Failed to load config data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL → Store 복원 (데이터 로드 완료 후 1회)
  useEffect(() => {
    if (loading || restoredRef.current || !store.allSteps.length || !allOptions.length) return;
    restoredRef.current = true;

    const restored: SelectedOption[] = [];
    for (const key of STEP_KEYS) {
      const optionId = searchParams.get(key);
      if (!optionId) continue;
      const id = Number(optionId);
      const step = store.allSteps.find((s) => s.step_key === key);
      const option = allOptions.find((o) => o.id === id);
      // 옵션이 실제로 해당 단계 소속일 때만 복원 (잘못된 URL 파라미터로
      // 엉뚱한 단계에 다른 옵션이 들어가는 것을 방지)
      if (step && option && option.step_id === step.id) {
        restored.push({ step, option });
      }
    }

    if (restored.length > 0) {
      store.restoreSelections(restored);

      const targetStep = searchParams.get('step');
      const pageParam = searchParams.get('page');
      // 최종 페이지에서 단계를 눌러 편집하러 온 경우(handleGoToStep이 page를 제거함)에만
      // step을 존중한다. step과 page=config/final이 함께 있으면 잔여(stale) 값이므로 무시.
      const deliberateEdit = targetStep !== null && pageParam === null;
      const complete = isSelectionComplete(restored);
      // 가격은 최종 제품 base_price만 사용하므로 합산값은 0으로 단순화
      const totalPrice = 0;

      if (pageParam === 'final' || (complete && !deliberateEdit)) {
        // 구성이 완료됐거나 최종 페이지 링크면 바로 최종 견적 페이지로 이동.
        // Store→URL 동기화가 page=final을 config로 덮어쓰지 못하게 차단.
        // (step 정리는 onComplete→handleComplete에서 함께 처리되므로 여기서 setSearchParams 하지 않는다)
        skipUrlSyncRef.current = true;
        onComplete(restored, totalPrice);
      } else if (targetStep !== null) {
        // 편집 의도이거나 미완성 + step 지정 → 해당 단계로 이동 후 잔여 step 정리
        store.goToStep(Number(targetStep));
        setSearchParams((prev) => { prev.delete('step'); return prev; }, { replace: true });
      }
    }
  }, [loading, store.allSteps, allOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Store → URL 반영 (selections 변경 시)
  useEffect(() => {
    if (!restoredRef.current) return;
    // 최종 페이지로 이동 중이면 URL 동기화를 건너뛴다 (page=final 보존).
    if (skipUrlSyncRef.current) return;

    setSearchParams((prev) => {
      // 기존 step_key params 제거
      for (const key of STEP_KEYS) {
        prev.delete(key);
      }
      // step 잔여값 제거 (편집 이동 후 URL에 남지 않도록)
      prev.delete('step');
      // 현재 selections 반영
      for (const sel of store.selections) {
        prev.set(sel.step.step_key, String(sel.option.id));
      }
      // 최종 페이지로 이동한 경우(page=final)는 덮어쓰지 않는다.
      if (prev.get('page') !== 'final') {
        prev.set('page', 'config');
      }
      return prev;
    }, { replace: true });
  }, [store.selections, setSearchParams]);

  // 현재 단계에 따른 옵션 필터링 — 교집합(reachability ∩ 의존성) 방식
  //  ① reachability: 조합 데이터(product_config)가 있으면 "현재 선택 + 이 옵션"이
  //     실제 제품 조합의 부분집합인 옵션만 남겨 막다른 경로를 원천 차단.
  //  ② 의존성(매트릭스): 위 결과에 config_dependency를 cumulative-AND로 추가 적용.
  //     관리자가 매트릭스에서 의존성을 끄면 해당 옵션이 실제로 숨겨진다.
  //  파생 의존성은 reachability의 상위집합이라 초기 동작은 기존과 동일하고,
  //  관리자가 매트릭스를 수정한 만큼만 추가로 좁혀진다.
  useEffect(() => {
    if (!store.currentStep || !allOptions.length) return;

    const stepOptions = allOptions.filter(
      (o) => o.step_id === store.currentStep!.id && o.is_active === 1
    );

    // 현재 단계보다 "앞선(상위)" 단계의 선택만 제약 조건으로 사용한다.
    // 최종 페이지에서 단계로 돌아와 편집할 때는 하위 단계 선택이 store에 남아 있는데,
    // 이를 제약에 포함하면 최종 제품 1개로만 좁혀져 옵션이 1개만 노출되는 버그가 생긴다.
    const currentKey = store.currentStep!.step_key;
    const activeKeys = store.activeSteps.map((s) => s.step_key);
    const currentKeyIndex = activeKeys.indexOf(currentKey);
    const selectedIds = store.selections
      .filter((s) => {
        const idx = activeKeys.indexOf(s.step.step_key);
        return idx !== -1 && idx < currentKeyIndex;
      })
      .map((s) => s.option.id);

    // ① reachability (조합 데이터가 있을 때만)
    let base = stepOptions;
    if (configSets.length > 0) {
      const reachable = stepOptions.filter((o) =>
        configSets.some((cs) => cs.has(o.id) && selectedIds.every((id) => cs.has(id)))
      );
      base = reachable.length ? reachable : stepOptions;
    }

    // ② 의존성 cumulative-AND
    //    이 단계로 들어오는 엣지를 가진 선행 선택(constraining)만 제약으로 사용하고,
    //    그 모든 부모가 허용하는 옵션만 남긴다.
    const baseIds = base.map((o) => o.id);
    const constraining = selectedIds.filter((pid) =>
      dependencies.some((d) => d.parent_option_id === pid && baseIds.includes(d.child_option_id))
    );
    if (constraining.length > 0) {
      const filtered = base.filter((o) =>
        constraining.every((pid) =>
          dependencies.some((d) => d.parent_option_id === pid && d.child_option_id === o.id)
        )
      );
      base = filtered.length ? filtered : base;
    }

    setAvailableOptions(base);
  }, [store.currentStep, store.currentStepIndex, store.activeSteps, store.selections, allOptions, dependencies, configSets]);

  const handleSelect = (option: ConfigOption) => {
    if (!store.currentStep) return;
    store.selectOption(store.currentStep, option);
  };

  const handleNext = () => {
    if (store.isComplete) {
      onComplete(store.selections, store.totalPrice);
    } else {
      store.goNext();
    }
  };

  const handleBack = () => {
    store.goBack();
  };

  const currentSelection = store.currentStep
    ? store.getSelectionForStep(store.currentStep.step_key)
    : null;

  // 엔진 출력/시동방식 단계는 옵션 자체 이미지가 없으므로,
  // (브랜드 + 출력 + 시동방식) 조합에 해당하는 엔진 이미지를 문맥적으로 표시한다.
  const resolveOptionImage = (option: ConfigOption): string | undefined => {
    const key = store.currentStep?.step_key;
    const brand = store.getSelectionForStep('engine_brand')?.option.option_name;
    const power = store.getSelectionForStep('engine_power')?.option.option_name;
    if (key === 'engine_power' && brand) {
      return engineImageUrl(brand, option.option_name, '수동리코일방식') || option.image_url;
    }
    if (key === 'start_method' && brand && power) {
      return engineImageUrl(brand, power, option.option_name) || option.image_url;
    }
    return option.image_url;
  };

  const isKo = lang === 'ko';

  if (loading) {
    return <LoadingWrapper>{isKo ? '로딩 중...' : 'Loading...'}</LoadingWrapper>;
  }

  const stepTitle = getStepTitle(store.currentStep, isKo);
  const stepSubtitle = getStepSubtitle(store.currentStep, store.currentStepIndex, isKo);

  return (
    <Wrapper>
      <Header lang={lang} onLangChange={onLangChange} onLogoClick={onLogoClick} />
      <Content>
        <StepProgress
          steps={store.activeSteps}
          currentIndex={store.currentStepIndex}
          completedCount={store.selections.length}
          onStepClick={(idx) => store.goToStep(idx)}
          lang={lang}
        />

        <TitleArea>
          <Title>
            {isKo ? (
              <>
                사용하실 분무기의
                <br />
                <Highlight>{stepTitle}</Highlight>{objectParticle(stepTitle)} 선택해 주세요
              </>
            ) : (
              <>
                Select the
                <br />
                <Highlight>{stepTitle}</Highlight> for your sprayer
              </>
            )}
          </Title>
        </TitleArea>

        <CardGrid $columns={availableOptions.length <= 3 ? availableOptions.length : 2}>
          {availableOptions.map((option) => (
            <OptionCard
              key={option.id}
              option={option}
              selected={currentSelection?.option.id === option.id}
              onClick={() => handleSelect(option)}
              imageUrl={resolveOptionImage(option)}
              lang={lang}
            />
          ))}
        </CardGrid>

        <CtaArea>
          {store.currentStepIndex > 0 && (
            <BackButton onClick={handleBack}>← {isKo ? '이전 단계' : 'Back'}</BackButton>
          )}
          <CtaButton onClick={handleNext} disabled={!currentSelection}>
            {store.isComplete
              ? (isKo ? '최종 견적 확인하기' : 'Review Final Quote')
              : (isKo ? '다음 단계로 진행하기' : 'Next')} →
          </CtaButton>
        </CtaArea>
      </Content>
      <ChatFab lang={lang} />
    </Wrapper>
  );
};

function getStepTitle(step: ConfigStep | null, isKo: boolean): string {
  if (!step) return '';
  if (isKo) {
    const map: Record<string, string> = {
      power_type: '동력 방식',
      power_source: '공급 전압',
      motor_origin: '모터 원산지',
      motor_capacity: '동력원 용량',
      frame_type: '프레임 형태',
      frame_sub_type: '좌대 형태',
      engine_brand: '엔진 브랜드',
      engine_power: '엔진 출력',
      start_method: '시동 방식',
      pump: '고압 펌프',
      pump_option: '펌프 옵션',
      hose: '호스',
    };
    return map[step.step_key] || step.step_name;
  }
  const mapEn: Record<string, string> = {
    power_type: 'Power Type',
    power_source: 'Power Source',
    motor_origin: 'Motor Origin',
    motor_capacity: 'Motor Power',
    frame_type: 'Frame Type',
    frame_sub_type: 'Mounting Type',
    engine_brand: 'Engine Brand',
    engine_power: 'Engine Power',
    start_method: 'Start Method',
    pump: 'High-Pressure Pump',
    pump_option: 'Pump Option',
    hose: 'High-Pressure Hose',
  };
  return mapEn[step.step_key] || step.step_name;
}

function getStepSubtitle(step: ConfigStep | null, index: number, isKo: boolean): string {
  if (!step) return '';
  if (isKo) return `${index + 1}단계: ${step.step_name}`;
  return `Step ${index + 1}: ${step.step_name_en || step.step_name}`;
}

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${theme.colors.background};

  &::before {
    content: '';
    position: absolute;
    top: ${theme.layout.headerHeight};
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/bg_cat.jpg') center / cover no-repeat;
    opacity: 0.04;
    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 45%, rgba(0, 0, 0, 0) 90%);
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 1) 45%, rgba(0, 0, 0, 0) 90%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 40px 60px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-size: 18px;
  color: ${theme.colors.textSecondary};
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${theme.colors.textSecondary};
`;

const TitleArea = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  line-height: 1.4;
  margin-bottom: 12px;
`;

const Highlight = styled.span`
  color: ${theme.colors.primary};
  text-decoration: underline;
  text-decoration-color: ${theme.colors.primary};
  text-underline-offset: 4px;
`;

// (브랜드, 출력, 시동방식) → 엔진 이미지 코드. 소스 엔진 7종에 대응.
// 01 우수S200FS, 02 센다이Sd225R(수동), 03 센다이키시동, 04 제논M230R(수동),
// 05 제논키시동, 06 혼다GP200(5.8), 07 혼다GX200(7)
function engineImageUrl(brand: string, power: string, start: string): string {
  const isKey = start.includes('키시동');
  let code = '';
  if (brand.includes('우수')) code = '01';
  else if (brand.includes('센다이')) code = isKey ? '03' : '02';
  else if (brand.includes('제논')) code = isKey ? '05' : '04';
  else if (brand.includes('혼다')) code = power.includes('5.8') ? '06' : '07';
  return code ? `/uploads/images/engine_${code}.png` : '';
}

const CardGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => Math.min($columns, 3)}, 1fr);
  gap: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const CtaArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 48px;
`;

const BackButton = styled.button`
  background: none;
  font-size: 16px;
  color: ${theme.colors.textSecondary};
  padding: 16px 24px;
  &:hover { color: ${theme.colors.textPrimary}; }
`;

const CtaButton = styled.button<{ disabled?: boolean }>`
  background: ${({ disabled }) => disabled ? theme.colors.stepperGray : '#F97316'};
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 600;
  padding: 16px 48px;
  border-radius: ${theme.layout.buttonRadius};
  transition: all 0.2s;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};

  &:hover {
    background: #EA580C;
    transform: scale(1.02);
  }
`;

export default ConfiguratorPage;
